import * as React from 'react';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Modal } from 'semantic-ui-react';

import { updateAlphaProperties } from '../../../actions/AlphasActions';
import { resetProperties } from '../../../actions/AlphasPropertiesAction';
import { addFlashMessages } from '../../../actions/FlashMessagesActions';
import analytics from '../../../shared/services/analytics/analytics';
import { AlphasAddToListContainer } from '../../../containers/AlphasAddToList';
import { localeData } from '../../../locales/data';
import { StateType } from '../../../reducers';
import {
  alphaPropertiesReducerSelector,
  getHasUpdatedPropertiesSelector,
} from '../../../reducers/AlphaPropertiesReducer/AlphaPropertiesSelector';
import { getUserTimeToLogoutSelector } from '../../../reducers/AuthenticationReducer/AuthenticationSelector';
import { addModifiers } from '../../../shared/helpers';
import { formatMessageHelper, FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { DispatchConnect, IntlMessagesError } from '../../../types';
import { CloseAlphaProperties } from '../../AlphaProperties/CloseAlphaProperties/CloseAlphaProperties';

import './AlphasDetails.scss';
import noop from 'lodash/noop';

interface HeaderAlphasDetailsProps {
  dateCreated: string;
  activeAlphaInfo: any;
  alphaName: string;
  stage: string;
  status: string;
  isConsultant: boolean;
  onFavoriteClick: () => void;
  onVisibilityClick: () => void;
  handleHide: () => void;
  openModalCloseProperties: boolean;
  handleCancelCloseAlphaProperties: () => void;
  handleNotSaveCloseAlphaProperties: () => void;
  handlerShowDetail: (alphaDetail: string) => void;
  nextAlphaDetail: string;
  isOthers?: boolean;
}

interface HeaderAlphasDetailsStateProps {
  userTimeToLogout: number | null;
  hasUpdatedProperties: boolean;
  alphaProperties: any;
}

interface HeaderAlphasDetailsStateDispatchers {
  dispatchFlashMessage: (errros: IntlMessagesError[]) => void;
  dispatchResetProperties: () => void;
  dispatchUpdateAlphaProperties: (alphaId: string, data: any) => Promise<any>;
}

type HeaderAlphasDetailsPropsGeneric = HeaderAlphasDetailsProps &
  InjectedIntlProps &
  HeaderAlphasDetailsStateDispatchers &
  HeaderAlphasDetailsStateProps;

export class HeaderAlphasDetailsIntl extends React.Component<HeaderAlphasDetailsPropsGeneric> {
  handleSaveAlphaProperties = () => {
    this.props.dispatchResetProperties();
    const alphaProperties = this.props.alphaProperties.toJS();
    const payload = {
      color: alphaProperties.color !== 'NONE' && alphaProperties.color.trim().length !== 0 ? alphaProperties.color : null,
      name: alphaProperties.name !== '' ? alphaProperties.name : null,
      tags: alphaProperties.tags,
      category: alphaProperties.category !== '' ? alphaProperties.category : null,
      description: alphaProperties.description !== '' ? alphaProperties.description : null,
    };
    const alphaId = this.props.activeAlphaInfo.get('id');
    this.props
      .dispatchUpdateAlphaProperties(alphaId, payload)
      .then((_: any) => {
        this.setState({ openModalCloseProperties: false }, () => {
          if (this.props.nextAlphaDetail !== '') {
            this.props.handlerShowDetail(this.props.nextAlphaDetail);
          } else {
            this.props.handleHide();
          }
        });
      })
      .catch(() => {
        this.props.dispatchFlashMessage([{ msg: 'actions.generic.error', defaultMessage: localeData.en['actions.generic.error'] }]);
      });
  };

  render() {
    const { dateCreated, activeAlphaInfo, onFavoriteClick, onVisibilityClick, alphaName, intl, isOthers = false, status } = this.props;

    const lowerCaseStatus = status.toLowerCase();

    return (
      <header className="alphas-details-content__header">
        <div className="alphas-details-content__header-icon-date">
          <div className={addModifiers('alphas-details-content__header-icon', [lowerCaseStatus])}>
            <FormattedMessageHelper tagName="span" id={`alphasDetails.status.${lowerCaseStatus}`} />
          </div>
          <div className={addModifiers('alphas-details-content__header-type', [activeAlphaInfo.get('type')])} />
          <div className="alphas-details-content__header-date">
            <FormattedMessageHelper
              tagName="span"
              id="alphasDetails.headerAlphasDetails.created"
              defaultMessage={localeData.en['alphasDetails.headerAlphasDetails.created']}
            />
            &nbsp;
            {dateCreated}
          </div>
        </div>
        <div className="alphas-details-content__header-title">{alphaName}</div>
        <div className={addModifiers('alpha__header-actions', [isOthers ? 'disabled' : ''])}>
          {activeAlphaInfo && (
            <AlphasAddToListContainer
              alphasData={[activeAlphaInfo.get('id')]}
              btnLabel={formatMessageHelper(intl, 'alphasAddToList.addAlphaToAList')}
              btnClasses="alphas-details-content__header-add"
              btnTitle={formatMessageHelper(intl, 'alphasAddToList.addAlphaToAList')}
              disabled={isOthers}
              onTrackOpen={() => analytics.trackCustomEvent('alphas_click_add_to_list', { event_label: 'detail' })}
            />
          )}
          <span
            className={addModifiers(
              activeAlphaInfo.get('favorite') ? 'alphas-details-content__header-star--selected' : 'alphas-details-content__header-star',
              [isOthers ? 'disabled' : '']
            )}
            onClick={isOthers ? noop : onFavoriteClick}
            title={
              activeAlphaInfo.get('favorite')
                ? formatMessageHelper(intl, 'alphas.removeFromFavorites.title')
                : formatMessageHelper(intl, 'alphas.addToFavorites.title')
            }
          />
          <span
            className={addModifiers(
              activeAlphaInfo.get('hidden')
                ? 'alphas-details-content__header-visualization'
                : 'alphas-details-content__header-visualization--selected',
              [isOthers ? 'disabled' : '']
            )}
            onClick={isOthers ? noop : onVisibilityClick}
            title={
              activeAlphaInfo.get('hidden')
                ? formatMessageHelper(intl, 'alphas.unhideAlpha.title')
                : formatMessageHelper(intl, 'alphas.hideAlpha.title')
            }
          />
          <Modal
            closeOnDimmerClick={false}
            closeOnEscape={false}
            closeIcon={true}
            open={this.props.openModalCloseProperties}
            onClose={this.props.handleCancelCloseAlphaProperties}
          >
            <Modal.Content>
              <CloseAlphaProperties
                handleCancelCloseAlphaProperties={this.props.handleCancelCloseAlphaProperties}
                handleNotSaveCloseAlphaProperties={this.props.handleNotSaveCloseAlphaProperties}
                handleSaveAlphaProperties={this.handleSaveAlphaProperties}
              />
            </Modal.Content>
          </Modal>

          <span className="alphas-details-content__header-close" onClick={this.props.handleHide} />
        </div>
      </header>
    );
  }
}

const mapStateToProps = (state: StateType): HeaderAlphasDetailsStateProps => ({
  // @ts-ignore
  userTimeToLogout: getUserTimeToLogoutSelector(state),
  // @ts-ignore
  hasUpdatedProperties: getHasUpdatedPropertiesSelector(state),
  alphaProperties: alphaPropertiesReducerSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<any>): HeaderAlphasDetailsStateDispatchers => ({
  dispatchFlashMessage: (errors: IntlMessagesError[]) => dispatch(addFlashMessages(errors)),
  dispatchResetProperties: () => dispatch(resetProperties()),
  dispatchUpdateAlphaProperties: async (alphaId: string, data: any) => dispatch(updateAlphaProperties(alphaId, data)),
});

export default connect<HeaderAlphasDetailsStateProps, DispatchConnect<HeaderAlphasDetailsStateDispatchers>, any, StateType>(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(HeaderAlphasDetailsIntl));
